import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { $$ } from "@tento-ui/fastdom";
import { Tracing } from "@tento-core/tracing";
import { LiveData } from "@tento-core/livedata";
import type { Option, RowLocalId } from "@tento-lona/sheets";
import type { SheetViewModel } from "@tento-lona";
import { SheetLegend } from "./sheet-legend.ts";
import type { SheetDelegate } from "./sheet-delegate.ts";
import type { SheetBinder } from "./sheet-index-renderer.ts";

/**
 * Hidden sentinel for measuring legend column width without disturbing
 * the visible layout. Lives inside the Sheet's shadow DOM so it inherits
 * the same CSS context (custom properties, font sizes, design tokens).
 *
 * Contains three SheetLegend children (body, header, footer) that are
 * bound with the same content as the real legends. Since the sentinel
 * has no minWidth constraint, its offsetWidth reflects the natural
 * content width.
 */
@component({ name: "std-legend-sentinel" })
export class LegendSentinel extends LonaWebComponent {
  #$body: SheetLegend = this.hydrate("body");
  #$header: SheetLegend = this.hydrate("header");
  #$footer: SheetLegend = this.hydrate("footer");

  /**
   * Bind sentinel legends with the same content as the real ones,
   * then measure and return the natural width in a fastdom mutate.
   */
  async measure(
    sheet: SheetViewModel.Surface<SheetBinder>,
    handle: SheetDelegate,
    activeRowOptions: LiveData.Readable<Option<RowLocalId>>,
    resizeDelegate: SheetLegend.ResizeDelegate,
  ): Promise<number> {
    this.#$header.isFrozen = true;
    this.#$footer.isFooter = true;

    const promises: Promise<unknown>[] = [];
    this.#$body.bindNow(
      sheet, handle, activeRowOptions, "other", resizeDelegate, promises,
    );
    this.#$header.bindNow(
      sheet, handle, activeRowOptions, "header", resizeDelegate, promises,
    );
    this.#$footer.bindNow(
      sheet, handle, activeRowOptions, "other", resizeDelegate, promises,
    );
    await Promise.all(promises);

    return new Promise<number>((resolve) => {
      $$.mutate(() => {
        const bodyW = this.#$body.offsetWidth;
        const headerW = this.#$header.offsetWidth;
        const footerW = this.#$footer.offsetWidth;
        const maxWidth = Math.max(bodyW, headerW, footerW);
        Tracing.log("legend-sentinel/measure", { bodyW, headerW, footerW, maxWidth });
        resolve(Math.max(150, Math.floor(maxWidth + 1)));
      });
    });
  }


  static $html = html`
    <std-sheet-legend id="body"></std-sheet-legend>
    <std-sheet-legend id="header"></std-sheet-legend>
    <std-sheet-legend id="footer"></std-sheet-legend>
  `;

  static $styles: Style.Compat[] = [css`
    :host {
      visibility: hidden;
      position: absolute;
      top: -9999px;
      left: 0;
      pointer-events: none;
      contain: layout style;
    }
    std-sheet-legend {
      display: block;
      width: max-content;
    }
  `];
}
